/**
 * <NavProgressBar /> — YouTube/GitHub-style thin progress bar at the top of the
 * viewport. Reflects React Query's aggregate fetching state so the user gets
 * a subtle "something is loading" signal during in-page data fetches.
 *
 * Design:
 *   - When `useIsFetching() > 0`, the bar animates from 15% → 80% width.
 *     It deliberately does NOT reach 100% while fetching (avoids misleading
 *     "we're done" look when more requests may follow).
 *   - When fetching drops to 0, we snap to 100% and fade out over 300ms.
 *   - The bar is 2px tall, absolutely positioned at the top of the main pane
 *     so it's visible regardless of which page is rendered.
 *   - Idle state is invisible (opacity 0) — zero visual cost when nothing is
 *     happening.
 *
 * This is complementary to the Suspense-level <PageLoader />:
 *   - PageLoader fires for chunk loads (full-page swap)
 *   - NavProgressBar fires for data fetches within an already-loaded page
 */
import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

const ACTIVE_MAX_PCT = 80;   // never reach 100 while still fetching
const DONE_FADE_MS   = 300;  // fade-out duration after drop to zero
const TICK_MS        = 180;  // animation tick

export default function NavProgressBar() {
  const fetching  = useIsFetching();
  const mutating  = useIsMutating();
  const active    = fetching + mutating > 0;

  const [width,   setWidth]   = useState(0);
  const [visible, setVisible] = useState(false);
  const fadeTimer = useRef<number | null>(null);
  const tickTimer = useRef<number | null>(null);

  useEffect(() => {
    if (active) {
      // Clear any pending fade-out and ramp up the bar.
      if (fadeTimer.current !== null) {
        window.clearTimeout(fadeTimer.current);
        fadeTimer.current = null;
      }
      setVisible(true);
      setWidth(w => Math.max(w, 15)); // start at 15%
      if (tickTimer.current === null) {
        tickTimer.current = window.setInterval(() => {
          setWidth(w => {
            if (w >= ACTIVE_MAX_PCT) return ACTIVE_MAX_PCT;
            // Ease toward ACTIVE_MAX_PCT — faster early, slower near cap.
            return w + Math.max(1, (ACTIVE_MAX_PCT - w) * 0.15);
          });
        }, TICK_MS);
      }
    } else {
      // Stop ticking, snap to 100, fade out.
      if (tickTimer.current !== null) {
        window.clearInterval(tickTimer.current);
        tickTimer.current = null;
      }
      setWidth(100);
      fadeTimer.current = window.setTimeout(() => {
        setVisible(false);
        setWidth(0);
        fadeTimer.current = null;
      }, DONE_FADE_MS);
    }

    return () => {
      if (tickTimer.current !== null) {
        window.clearInterval(tickTimer.current);
        tickTimer.current = null;
      }
    };
  }, [active]);

  return (
    <div
      aria-hidden="true"
      className="fixed top-0 left-0 right-0 h-[2px] z-50 pointer-events-none"
      style={{
        opacity:    visible ? 1 : 0,
        transition: `opacity ${DONE_FADE_MS}ms ease-out`,
      }}
    >
      <div
        className="h-full bg-brand"
        style={{
          width:      `${width}%`,
          transition: "width 200ms ease-out",
          boxShadow:  "0 0 8px rgba(123, 138, 255, 0.5)",
        }}
      />
    </div>
  );
}
